import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { searchTerms } from "@/lib/search-text";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  ANUNCIO_ORIGENES,
  ANUNCIO_VISIBILIDADES,
  ESTADOS_INMUEBLE,
  NIVELES_TRANSITO,
} from "@/lib/anuncios.functions";
import { applyAsignadoFilter } from "@/lib/scope-filtro";
import { DEMANDA_SEGUIMIENTOS } from "@/lib/demanda-seguimiento";

const Id = z.object({ id: z.string().uuid() });

// Columnas booleanas de las demandas (mismo nombre que la columna en anuncios).
// null = indiferente; true = exigido; false = excluyente.
export const DEMANDA_BOOL_COLS = [
  "a_pie_de_calle",
  "equipamiento_incluido",
  "t_esquinero",
  "t_mucha_luz",
  "t_amueblado",
  "t_cocina",
  "t_agua",
  "t_aire_acondicionado",
  "t_alarma",
  "t_parking",
  "t_acceso_movilidad_reducida",
  "t_salida_emergencia",
  "h_terraza",
  "h_camara_frigorifica",
  "h_obrador",
  "h_salida_humos",
  "n_muelle_carga",
  "n_vado",
] as const;
export type DemandaBoolCol = (typeof DEMANDA_BOOL_COLS)[number];

const uuidArr = z.array(z.string().uuid()).default([]);

const boolNullable = z.boolean().nullable().optional();
const numNullable = z.number().nullable().optional();
const intNullable = z.number().int().nullable().optional();

const BoolFieldsShape = Object.fromEntries(
  DEMANDA_BOOL_COLS.map((k) => [k, boolNullable]),
) as Record<DemandaBoolCol, typeof boolNullable>;

const RangeFieldsShape = {
  t_ancho_fachada_min: numNullable,
  t_ancho_fachada_max: numNullable,
  h_aforo_min: intNullable,
  h_aforo_max: intNullable,
  h_sillas_terraza_min: intNullable,
  h_sillas_terraza_max: intNullable,
  n_altura_min: numNullable,
  n_altura_max: numNullable,
};

const DemandaInput = z.object({
  titulo: z.string().trim().max(200).nullable().optional(),
  notas: z.string().trim().max(5000).nullable().optional(),
  notas_internas: z.string().trim().max(5000).nullable().optional(),
  proyecto_momento: z.string().trim().max(2000).nullable().optional(),
  pago_estructura: z.string().trim().max(2000).nullable().optional(),
  dependencia_operacion: z.string().trim().max(2000).nullable().optional(),
  necesidad_local: z.string().trim().max(2000).nullable().optional(),
  contacto_id: z.string().uuid(),
  agencia_id: z.string().uuid().nullable().optional(),
  activa: z.boolean().default(true),
  // Correos de coincidencias: en el CRM nace apagado; el conector lo enciende.
  notificar_coincidencias: z.boolean().default(false),
  estado: z.enum(["incompleta","iniciada","en_visitas","negociacion","arras","completada","cancelada","archivada"]).default("incompleta"),
  seguimiento: z.enum(DEMANDA_SEGUIMIENTOS).default("sin_atender"),

  operacion_ids: uuidArr,
  tipo_local_ids: uuidArr,
  licencia_ids: uuidArr,
  uso_local_ids: uuidArr,
  provincia_ids: uuidArr,
  ciudad_ids: uuidArr,
  distrito_ids: uuidArr,
  barrio_ids: uuidArr,
  cp: z.string().trim().max(10).nullable().optional(),

  precio_alquiler_min: z.number().nonnegative().nullable().optional(),
  precio_alquiler_max: z.number().nonnegative().nullable().optional(),
  precio_traspaso_min: z.number().nonnegative().nullable().optional(),
  precio_traspaso_max: z.number().nonnegative().nullable().optional(),
  precio_venta_min: z.number().nonnegative().nullable().optional(),
  precio_venta_max: z.number().nonnegative().nullable().optional(),
  rent_bruta_min: z.number().nonnegative().nullable().optional(),
  rent_bruta_max: z.number().nonnegative().nullable().optional(),
  superficie_min: z.number().nonnegative().nullable().optional(),
  superficie_max: z.number().nonnegative().nullable().optional(),
  aseos_min: z.number().int().nonnegative().nullable().optional(),
  parking_plazas_min: z.number().int().nonnegative().nullable().optional(),
  mesas_terraza_min: z.number().int().nonnegative().nullable().optional(),
  mesas_terraza_max: z.number().int().nonnegative().nullable().optional(),

  visibilidades: z.array(z.enum(ANUNCIO_VISIBILIDADES)).default([]),
  origenes: z.array(z.enum(ANUNCIO_ORIGENES)).default([]),
  estado_inmueble: z.array(z.enum(ESTADOS_INMUEBLE)).default([]),
  niveles_transito: z.array(z.enum(NIVELES_TRANSITO)).default([]),

  ...BoolFieldsShape,
  ...RangeFieldsShape,
});


export type DemandaInput = z.infer<typeof DemandaInput>;

async function userStaffInfo(userId: string | null) {
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const [{ data: roles }, { data: members }] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id").eq("user_id", userId),
  ]);
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isAdmin = rs.includes("admin");
  const isStaff = isAdmin || rs.includes("customer_service") || rs.includes("agente");
  const agenciaIds = (members ?? []).map((m: { agencia_id: string }) => m.agencia_id);
  if (!isStaff && agenciaIds.length === 0) throw new Error("Forbidden");
  return { isStaff, isAdmin, agenciaIds };
}

export const getDemandasAdminContext = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  const info = await userStaffInfo(userId);
  return info;
});

// ───────── List ─────────
export const listDemandas = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z
      .object({
        q: z.string().trim().max(120).optional(),
        agenciaVista: z.string().nullable().optional(),
        asignado: z.string().nullable().optional(),
        /** Editor en modo "Todo el equipo": ve las demandas de sus compañeros sin datos del cliente. */
        ver_equipo: z.boolean().optional(),
        /** Solo las demandas de este contacto (ficha de contacto). */
        contacto_id: z.string().uuid().optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    const info = await userStaffInfo(userId);

    if (!info.isStaff && info.agenciaIds.length === 0) return [];
    const { resolveScopePermisos } = await import("@/lib/scope-permisos.server");
    const permisos = await resolveScopePermisos();
    const modoEquipo = !permisos.puedeVerAgencia && !!data.ver_equipo;
    const asignadoFiltro = permisos.puedeVerAgencia || modoEquipo ? (data.asignado ?? null) : permisos.userId;


    const { fetchAllPaged } = await import("@/lib/pagination.server");
    const rows = await fetchAllPaged<any>((from, to) => {
      let q = sb
        .from("demandas")
        .select(
          "id, referencia, titulo, contacto_id, agencia_id, asignado_a, activa, notificar_coincidencias, estado, seguimiento, origen, provincia_ids, visibilidades, calidad, match_count, last_matched_at, created_at, updated_at",
        )
        .order("created_at", { ascending: false })
        .range(from, to);

      if (!info.isStaff) q = q.in("agencia_id", info.agenciaIds);
      else if (data.agenciaVista === "sin_agencia") q = q.is("agencia_id", null);
      else if (data.agenciaVista) q = q.eq("agencia_id", data.agenciaVista);
      q = applyAsignadoFilter(q, asignadoFiltro);
      if (data.contacto_id) q = q.eq("contacto_id", data.contacto_id);

      if (data.q) for (const t of searchTerms(data.q)) q = q.ilike("busqueda", `%${t}%`);
      return q;
    });


    const contactoIds = Array.from(new Set((rows ?? []).map((r) => r.contacto_id).filter(Boolean)));
    let contactosById: Record<string, { nombre: string | null; apellidos: string | null; email: string | null }> = {};
    if (contactoIds.length) {
      // Vista de clientes: incluye los cedidos a la agencia por la propia demanda
      // (la RLS de `contactos` solo deja ver la cartera propia).
      const { clientesPorIds } = await import("@/lib/clientes-cedidos.server");
      const cs = await clientesPorIds(contactoIds as string[]);
      contactosById = Object.fromEntries(
        Array.from(cs.values()).map((c) => [c.id, { nombre: c.nombre, apellidos: c.apellidos, email: c.email }]),
      );
    }
    const agenciaIds = Array.from(new Set((rows ?? []).map((r) => r.agencia_id).filter((x): x is string => !!x)));
    let agenciasById: Record<string, string> = {};
    if (agenciaIds.length) {
      const { data: ags } = await sb.from("agencias").select("id, nombre").in("id", agenciaIds);
      agenciasById = Object.fromEntries((ags ?? []).map((a) => [a.id, a.nombre ?? ""]));
    }
    const provIds = Array.from(new Set((rows ?? []).flatMap((r) => (r.provincia_ids ?? []) as string[])));
    let provById: Record<string, string> = {};
    if (provIds.length) {
      const { data: ps } = await sb.from("provincias").select("id, nombre").in("id", provIds);
      provById = Object.fromEntries((ps ?? []).map((p) => [p.id, p.nombre ?? ""]));
    }
    return (rows ?? []).map((r) => {
      // Demanda de un compañero (modo equipo): sin datos personales del cliente.
      const deCompanero = modoEquipo && r.asignado_a !== permisos.userId;
      return {
        ...r,
        provincia_nombres: ((r.provincia_ids ?? []) as string[]).map((id) => provById[id]).filter(Boolean),
        contactos: deCompanero ? null : (contactosById[r.contacto_id] ?? null),
        contacto_id: deCompanero ? null : r.contacto_id,
        de_companero: deCompanero,
        agencia_nombre: r.agencia_id ? agenciasById[r.agencia_id] ?? null : null,
      };
    });
  });

// ───────── Get ─────────
export const getDemanda = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertCanViewDemanda(await resolveCurrentUserId(), data.id);
    const sb = getSupabaseAdmin();
    const { data: row, error } = await sb
      .from("demandas")
      .select("*")
      .eq("id", data.id)
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

// ───────── Create ─────────
export const createDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => DemandaInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    const info = await userStaffInfo(userId);

    let payload: Record<string, unknown> = { ...data, created_by: userId, origen: "manual" };
    // Si no es staff, forzar agencia a una de las suyas
    if (!info.isStaff) {
      if (info.agenciaIds.length === 0) throw new Error("Sin agencia asignada");
      if (!data.agencia_id || !info.agenciaIds.includes(data.agencia_id)) {
        payload.agencia_id = info.agenciaIds[0];
      }
    }
    // Autoasignación al creador (staff o miembro editor+ de la agencia).
    const { resolveAsignadoInicial } = await import("@/lib/asignacion.server");
    payload.asignado_a = await resolveAsignadoInicial(
      userId,
      (payload.agencia_id as string | null | undefined) ?? null,
      (payload.asignado_a as string | null | undefined) ?? null,
    );


    const { data: row, error } = await sb
      .from("demandas")
      .insert(payload)
      .select("id, referencia")
      .single();
    if (error) throw new Error(error.message);
    return { ok: true, id: row.id, referencia: row.referencia };
  });

/**
 * Ver una demanda (y sus coincidencias): staff, miembros de su agencia o el
 * propio cliente dueño de la demanda. Devuelve el contexto del usuario para
 * decidir qué datos de los anuncios puede ver.
 */
/** Alias y dirección de un anuncio solo los ve el staff o su propia agencia. */
function anuncioOcultoPara(
  viewer: { isStaff: boolean; agenciaIds: string[] },
  anuncioAgenciaId: string | null,
) {
  if (viewer.isStaff) return false;
  return !anuncioAgenciaId || !viewer.agenciaIds.includes(anuncioAgenciaId);
}

async function assertCanViewDemanda(userId: string | null, demandaId: string) {
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const [{ data: roles }, { data: members }, { data: dem }] = await Promise.all([
    sb.from("user_roles").select("role").eq("user_id", userId),
    sb.from("agencia_miembros").select("agencia_id").eq("user_id", userId),
    sb.from("demandas").select("agencia_id, contacto_id").eq("id", demandaId).maybeSingle(),
  ]);
  if (!dem) throw new Error("Demanda no encontrada");
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isStaff = rs.includes("admin") || rs.includes("customer_service") || rs.includes("agente");
  const agenciaIds = (members ?? []).map((m: { agencia_id: string }) => m.agencia_id);
  if (isStaff) return { isStaff, agenciaIds };
  if (dem.agencia_id && agenciaIds.includes(dem.agencia_id)) return { isStaff, agenciaIds };
  if (dem.contacto_id) {
    const { data: c } = await sb
      .from("contactos")
      .select("user_id")
      .eq("id", dem.contacto_id)
      .maybeSingle();
    if (c?.user_id === userId) return { isStaff, agenciaIds };
  }
  throw new Error("No autorizado");
}

async function assertCanEdit(id: string) {
  const userId = await resolveCurrentUserId();
  const info = await userStaffInfo(userId);
  if (info.isStaff) return;
  const sb = getSupabaseAdmin();
  const { data: row } = await sb
    .from("demandas")
    .select("agencia_id")
    .eq("id", id)
    .maybeSingle();
  if (!row || !row.agencia_id || !info.agenciaIds.includes(row.agencia_id)) {
    throw new Error("No autorizado");
  }
}

// ───────── Resumen de una demanda de un compañero (solo lectura) ─────────
/** Lo que busca el cliente, sin sus datos personales ni notas internas. */
export const getDemandaResumenEquipo = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const userId = await resolveCurrentUserId();
    const info = await userStaffInfo(userId);
    const sb = getSupabaseAdmin();
    const { data: dem, error } = await sb
      .from("demandas")
      .select(
        "id, referencia, titulo, estado, agencia_id, asignado_a, provincia_ids, ciudad_ids, operacion_ids, tipo_local_ids, uso_local_ids, precio_alquiler_min, precio_alquiler_max, precio_traspaso_min, precio_traspaso_max, precio_venta_min, precio_venta_max, superficie_min, superficie_max, match_count",
      )
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!dem) throw new Error("Demanda no encontrada");
    if (!info.isStaff && !(dem.agencia_id && info.agenciaIds.includes(dem.agencia_id))) {
      throw new Error("No autorizado");
    }
    const nombres = async (tabla: string, ids: string[] | null) => {
      if (!ids?.length) return [] as string[];
      const { data: rows } = await sb.from(tabla as never).select("nombre").in("id", ids);
      return ((rows ?? []) as Array<{ nombre: string }>).map((r) => r.nombre);
    };
    const [provincias, ciudades, operaciones, tipos, usos, agente] = await Promise.all([
      nombres("provincias", dem.provincia_ids as string[]),
      nombres("ciudades", dem.ciudad_ids as string[]),
      nombres("operaciones", dem.operacion_ids as string[]),
      nombres("tipos_local", dem.tipo_local_ids as string[]),
      nombres("usos_local", dem.uso_local_ids as string[]),
      dem.asignado_a
        ? sb.from("contactos").select("nombre, apellidos").eq("user_id", dem.asignado_a).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    const ag = (agente as { data: { nombre: string | null; apellidos: string | null } | null }).data;
    return {
      id: dem.id as string,
      referencia: dem.referencia as string,
      titulo: dem.titulo as string | null,
      estado: dem.estado as string,
      agente: ag ? [ag.nombre, ag.apellidos].filter(Boolean).join(" ") : null,
      provincias,
      ciudades,
      operaciones,
      tipos,
      usos,
      precio_alquiler: [dem.precio_alquiler_min, dem.precio_alquiler_max] as Array<number | null>,
      precio_traspaso: [dem.precio_traspaso_min, dem.precio_traspaso_max] as Array<number | null>,
      precio_venta: [dem.precio_venta_min, dem.precio_venta_max] as Array<number | null>,
      superficie: [dem.superficie_min, dem.superficie_max] as Array<number | null>,
      match_count: (dem.match_count as number | null) ?? 0,
    };
  });

// ───────── Crear demanda desde una solicitud ─────────
/** Margen alrededor del precio y la superficie del anuncio al crear la demanda. */
const MARGEN_DESDE_ANUNCIO = 0.2;

export const createDemandaDesdeSolicitud = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ solicitud_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    const info = await userStaffInfo(userId);

    const { data: sol, error: eS } = await sb
      .from("solicitudes")
      .select(
        "id, contacto_id, agencia_id, demanda_id, origen, " +
          "anuncio:anuncios(id, referencia, agencia_id, provincia_id, ciudad_id, operacion_id, tipo_local_id, precio_alquiler, precio_traspaso, precio_venta, t_superficie, anuncio_usos_local(uso_local_id))",
      )
      .eq("id", data.solicitud_id)
      .maybeSingle();
    if (eS) throw new Error(eS.message);
    const s = sol as unknown as {
      id: string;
      contacto_id: string;
      agencia_id: string | null;
      demanda_id: string | null;
      origen: string | null;
      anuncio: {
        id: string;
        referencia: string | null;
        agencia_id: string | null;
        provincia_id: string | null;
        ciudad_id: string | null;
        operacion_id: string | null;
        tipo_local_id: string | null;
        precio_alquiler: number | null;
        precio_traspaso: number | null;
        precio_venta: number | null;
        t_superficie: number | null;
        anuncio_usos_local: Array<{ uso_local_id: string }> | null;
      } | null;
    } | null;
    if (!s) throw new Error("Solicitud no encontrada");
    if (s.demanda_id) throw new Error("Esta solicitud ya tiene una demanda");

    const agenciaSolicitud = s.agencia_id ?? s.anuncio?.agencia_id ?? null;
    let agenciaId = agenciaSolicitud;
    if (!info.isStaff) {
      const permitidas = [s.agencia_id, s.anuncio?.agencia_id].filter(Boolean) as string[];
      if (!permitidas.some((a) => info.agenciaIds.includes(a))) throw new Error("No autorizado");
      if (!agenciaId || !info.agenciaIds.includes(agenciaId)) agenciaId = info.agenciaIds[0];
    }

    const a = s.anuncio;
    const rango = (v: number | null | undefined) =>
      v && v > 0
        ? { min: Math.round(v * (1 - MARGEN_DESDE_ANUNCIO)), max: Math.round(v * (1 + MARGEN_DESDE_ANUNCIO)) }
        : null;
    const alq = rango(a?.precio_alquiler);
    const tra = rango(a?.precio_traspaso);
    const ven = rango(a?.precio_venta);
    const sup = rango(a?.t_superficie);
    const base = DemandaInput.parse({
      contacto_id: s.contacto_id,
      agencia_id: agenciaId,
      titulo: a?.referencia ? `Desde la solicitud sobre ${a.referencia}` : "Desde una solicitud",
      provincia_ids: a?.provincia_id ? [a.provincia_id] : [],
      ciudad_ids: a?.ciudad_id ? [a.ciudad_id] : [],
      operacion_ids: a?.operacion_id ? [a.operacion_id] : [],
      tipo_local_ids: a?.tipo_local_id ? [a.tipo_local_id] : [],
      uso_local_ids: (a?.anuncio_usos_local ?? []).map((u) => u.uso_local_id),
      precio_alquiler_min: alq?.min ?? null,
      precio_alquiler_max: alq?.max ?? null,
      precio_traspaso_min: tra?.min ?? null,
      precio_traspaso_max: tra?.max ?? null,
      precio_venta_min: ven?.min ?? null,
      precio_venta_max: ven?.max ?? null,
      superficie_min: sup?.min ?? null,
      superficie_max: sup?.max ?? null,
    });

    const { resolveAsignadoInicial } = await import("@/lib/asignacion.server");
    const asignadoA = await resolveAsignadoInicial(userId, agenciaId ?? null, null);
    const { data: row, error } = await sb
      .from("demandas")
      .insert({
        ...base,
        created_by: userId,
        asignado_a: asignadoA,
        // La demanda nace por la misma puerta que la solicitud.
        origen: (s.origen as never) ?? "manual",
      } as never)
      .select("id, referencia")
      .single();
    if (error) throw new Error(error.message);

    const { error: eU } = await sb.from("solicitudes").update({ demanda_id: row.id }).eq("id", s.id);
    if (eU) throw new Error(eU.message);

    try {
      const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
      await recomputeDemandaMatchesInternal(row.id);
    } catch (e) {
      console.error("[createDemandaDesdeSolicitud] recompute", e);
    }
    return { ok: true as const, id: row.id as string, referencia: row.referencia as string };
  });

// ───────── Update ─────────
export const updateDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => DemandaInput.extend(Id.shape).parse(d))
  .handler(async ({ data }) => {
    await assertCanEdit(data.id);
    const sb = getSupabaseAdmin();
    const { id, ...rest } = data;
    const { data: before } = await sb
      .from("demandas")
      .select(["asignado_a", ...DEMANDA_MATCH_COLS].join(", "))
      .eq("id", id)
      .maybeSingle();
    const prev = (before ?? null) as Record<string, unknown> | null;
    const { data: after, error } = await sb
      .from("demandas")
      .update(rest)
      .eq("id", id)
      .select("id, asignado_a")
      .single();
    if (error) throw new Error(error.message);

    // Si cambian los criterios, la caché (y `match_count`) queda obsoleta:
    // sin esto el listado seguía mostrando las coincidencias de los criterios
    // anteriores hasta pulsar «Actualizar coincidencias».
    let matchesRecomputed = false;
    if (demandaCriteriaChanged(prev, rest)) {
      try {
        const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
        await recomputeDemandaMatchesInternal(id);
        matchesRecomputed = true;
      } catch (e) {
        console.error("[updateDemanda] recompute", id, e);
      }
    }
    return {
      ok: true,
      asignado_a: (after?.asignado_a as string | null) ?? null,
      asignado_a_prev: (prev?.asignado_a as string | null) ?? null,
      matches_recomputed: matchesRecomputed,
    };
  });

// ───────── Reasignar ─────────
export const assignDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({ id: z.string().uuid(), asignado_a: z.string().uuid().nullable() })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertCanEdit(data.id);
    const sb = getSupabaseAdmin();
    const { error } = await sb
      .from("demandas")
      .update({ asignado_a: data.asignado_a })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


// ───────── Delete ─────────
export const deleteDemanda = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertCanEdit(data.id);
    const sb = getSupabaseAdmin();
    const { error } = await sb.from("demandas").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Coincidencias (live) ─────────
export type DemandaRow = {
  operacion_ids: string[];
  tipo_local_ids: string[];
  licencia_ids: string[];
  uso_local_ids: string[];
  provincia_ids: string[];
  ciudad_ids: string[];
  distrito_ids: string[];
  barrio_ids: string[];
  cp: string | null;
  precio_alquiler_min: number | null;
  precio_alquiler_max: number | null;
  precio_traspaso_min: number | null;
  precio_traspaso_max: number | null;
  precio_venta_min: number | null;
  precio_venta_max: number | null;
  rent_bruta_min: number | null;
  rent_bruta_max: number | null;
  superficie_min: number | null;
  superficie_max: number | null;
  aseos_min: number | null;
  parking_plazas_min: number | null;
  mesas_terraza_min: number | null;
  mesas_terraza_max: number | null;
  t_ancho_fachada_min: number | null;
  t_ancho_fachada_max: number | null;
  h_aforo_min: number | null;
  h_aforo_max: number | null;
  h_sillas_terraza_min: number | null;
  h_sillas_terraza_max: number | null;
  n_altura_min: number | null;
  n_altura_max: number | null;
  visibilidades: string[];
  origenes: string[];
  estado_inmueble: string[];
  niveles_transito: string[];
} & Partial<Record<DemandaBoolCol, boolean | null>>;

// Columnas de la demanda que intervienen en el matching (las que lee
// `applyDemandaFilters`). Si cambia alguna, la caché de coincidencias queda
// obsoleta y hay que recalcularla.
export const DEMANDA_MATCH_COLS = [
  "operacion_ids",
  "tipo_local_ids",
  "licencia_ids",
  "uso_local_ids",
  "provincia_ids",
  "ciudad_ids",
  "distrito_ids",
  "barrio_ids",
  "cp",
  "precio_alquiler_min",
  "precio_alquiler_max",
  "precio_traspaso_min",
  "precio_traspaso_max",
  "precio_venta_min",
  "precio_venta_max",
  "rent_bruta_min",
  "rent_bruta_max",
  "superficie_min",
  "superficie_max",
  "aseos_min",
  "parking_plazas_min",
  "mesas_terraza_min",
  "mesas_terraza_max",
  "t_ancho_fachada_min",
  "t_ancho_fachada_max",
  "h_aforo_min",
  "h_aforo_max",
  "h_sillas_terraza_min",
  "h_sillas_terraza_max",
  "n_altura_min",
  "n_altura_max",
  "visibilidades",
  "origenes",
  "estado_inmueble",
  "niveles_transito",
  ...DEMANDA_BOOL_COLS,
] as const satisfies ReadonlyArray<keyof DemandaRow>;

// Normaliza un valor de criterio para comparar lo guardado en BD con lo que
// llega del formulario: Postgres devuelve `numeric` como string ("1000") y
// el orden de los arrays no afecta al matching.
function normCriterio(v: unknown): string {
  if (v == null || v === "") return "";
  if (Array.isArray(v)) return v.map(String).sort().join(",");
  if (typeof v === "number" || (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v)))) {
    return String(Number(v));
  }
  return String(v);
}

export function demandaCriteriaChanged(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown>,
): boolean {
  if (!before) return true;
  return DEMANDA_MATCH_COLS.some(
    (col) => col in after && normCriterio(before[col]) !== normCriterio(after[col]),
  );
}

// Construye una query de anuncios a partir de la fila demanda
export async function fetchBlockedAgenciaIds(
  sb: ReturnType<typeof getSupabaseAdmin>,
): Promise<string[]> {
  const { data } = await sb.from("agencias").select("id").eq("estado", "bloqueada");
  return ((data ?? []) as Array<{ id: string }>).map((r) => r.id);
}

export function applyDemandaFilters(
  sb: ReturnType<typeof getSupabaseAdmin>,
  d: DemandaRow,
  selectCols: string,
  opts: {
    limit?: number;
    count?: "exact";
    head?: boolean;
    blockedAgenciaIds?: string[];
  } = {},
) {
  // Si hay uso_local_ids, aplicamos un inner join embebido para evitar URLs
  // gigantes con .in("id", [...1000+ uuids]) que rompen PostgREST (Bad Request).
  const usoIds = d.uso_local_ids ?? [];
  const effectiveSelect = usoIds.length
    ? `${selectCols}, anuncio_usos_local!inner(uso_local_id)`
    : selectCols;

  let q = sb
    .from("anuncios")
    .select(effectiveSelect, opts.count ? { count: opts.count, head: opts.head } : undefined)
    .eq("estado", "publicado");

  // Excluir anuncios cuya agencia esté bloqueada.
  const blocked = opts.blockedAgenciaIds ?? [];
  // `agencia_id NOT IN (...)` es NULL (no TRUE) para anuncios sin agencia, así
  // que hay que admitirlos explícitamente, como hace el portal.
  if (blocked.length) q = q.or(`agencia_id.is.null,agencia_id.not.in.(${blocked.join(",")})`);

  // Desempate por id: el recompute pagina esta query y un orden no único
  // podría saltarse o repetir filas entre páginas.
  if (!opts.head) q = q.order("created_at", { ascending: false }).order("id", { ascending: true });
  if (opts.limit != null) q = q.limit(opts.limit);

  if (usoIds.length) q = q.in("anuncio_usos_local.uso_local_id", usoIds);


  if (d.operacion_ids?.length) q = q.in("operacion_id", d.operacion_ids);
  if (d.tipo_local_ids?.length) q = q.in("tipo_local_id", d.tipo_local_ids);
  if (d.licencia_ids?.length) q = q.in("licencia_id", d.licencia_ids);
  if (d.provincia_ids?.length) q = q.in("provincia_id", d.provincia_ids);
  if (d.ciudad_ids?.length) q = q.in("ciudad_id", d.ciudad_ids);
  if (d.distrito_ids?.length) q = q.in("distrito_id", d.distrito_ids);
  if (d.barrio_ids?.length) q = q.in("barrio_id", d.barrio_ids);
  if (d.cp) q = q.ilike("cp", `${d.cp}%`);

  if (d.visibilidades?.length) q = q.in("visibilidad", d.visibilidades);
  if (d.origenes?.length) q = q.in("origen", d.origenes);
  if (d.estado_inmueble?.length) q = q.in("estado_inmueble", d.estado_inmueble);
  if (d.niveles_transito?.length) q = q.in("nivel_transito", d.niveles_transito);

  // Helpers: los rangos deben ser tolerantes con NULL en el anuncio. Si un
  // anuncio no tiene el dato (aforo, aseos, mesas de terraza, superficie…),
  // no debe excluirse: el portal tampoco lo excluye, y así demanda y portal
  // devuelven el mismo conjunto de coincidencias.
  const gteOrNull = (col: string, v: number) => `${col}.gte.${v},${col}.is.null`;
  const lteOrNull = (col: string, v: number) => `${col}.lte.${v},${col}.is.null`;

  if (d.precio_alquiler_min != null) q = q.or(gteOrNull("precio_alquiler", d.precio_alquiler_min));
  if (d.precio_alquiler_max != null) q = q.or(lteOrNull("precio_alquiler", d.precio_alquiler_max));
  if (d.precio_traspaso_min != null) q = q.or(gteOrNull("precio_traspaso", d.precio_traspaso_min));
  if (d.precio_traspaso_max != null) q = q.or(lteOrNull("precio_traspaso", d.precio_traspaso_max));
  if (d.precio_venta_min != null) q = q.or(gteOrNull("precio_venta", d.precio_venta_min));
  if (d.precio_venta_max != null) q = q.or(lteOrNull("precio_venta", d.precio_venta_max));
  if (d.rent_bruta_min != null) q = q.or(gteOrNull("rent_bruta", d.rent_bruta_min));
  if (d.rent_bruta_max != null) q = q.or(lteOrNull("rent_bruta", d.rent_bruta_max));
  if (d.superficie_min != null) q = q.or(gteOrNull("t_superficie", d.superficie_min));
  if (d.superficie_max != null) q = q.or(lteOrNull("t_superficie", d.superficie_max));
  if (d.aseos_min != null) q = q.or(gteOrNull("t_num_aseos", d.aseos_min));
  if (d.parking_plazas_min != null) q = q.or(gteOrNull("t_parking_plazas", d.parking_plazas_min));
  if (d.mesas_terraza_min != null) q = q.or(gteOrNull("h_mesas_terraza", d.mesas_terraza_min));
  if (d.mesas_terraza_max != null) q = q.or(lteOrNull("h_mesas_terraza", d.mesas_terraza_max));
  if (d.t_ancho_fachada_min != null) q = q.or(gteOrNull("t_ancho_fachada", d.t_ancho_fachada_min));
  if (d.t_ancho_fachada_max != null) q = q.or(lteOrNull("t_ancho_fachada", d.t_ancho_fachada_max));
  if (d.h_aforo_min != null) q = q.or(gteOrNull("h_aforo", d.h_aforo_min));
  if (d.h_aforo_max != null) q = q.or(lteOrNull("h_aforo", d.h_aforo_max));
  if (d.h_sillas_terraza_min != null) q = q.or(gteOrNull("h_sillas_terraza", d.h_sillas_terraza_min));
  if (d.h_sillas_terraza_max != null) q = q.or(lteOrNull("h_sillas_terraza", d.h_sillas_terraza_max));
  if (d.n_altura_min != null) q = q.or(gteOrNull("n_altura", d.n_altura_min));
  if (d.n_altura_max != null) q = q.or(lteOrNull("n_altura", d.n_altura_max));


  for (const col of DEMANDA_BOOL_COLS) {
    const v = d[col];
    if (typeof v === "boolean") q = q.eq(col, v);
  }
  return q;
}
export const getDemandaMatches = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        limit: z.number().int().min(1).max(5000).default(5000),
        includeDetails: z.boolean().optional().default(false),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const viewer = await assertCanViewDemanda(context.userId, data.id);
    const sb = getSupabaseAdmin();

    // Sirve desde caché (recalculada por cron diario o botón manual).
    // Si nunca se ha calculado, hace un cómputo inicial on-demand.
    const { data: dem } = await sb
      .from("demandas")
      .select("match_count, last_matched_at, matches_seen_at")
      .eq("id", data.id)
      .single();

    if (!dem?.last_matched_at) {
      const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
      await recomputeDemandaMatchesInternal(data.id);
    }

    const { readDemandaMatchesCache } = await import("@/lib/demandas.server");
    const readCache = () => readDemandaMatchesCache(sb, data.id, data.limit);
    let cacheRows = await readCache();

    let ids = ((cacheRows ?? []) as Array<{ anuncio_id: string }>).map((r) => r.anuncio_id);

    const { data: d2 } = await sb
      .from("demandas")
      .select("match_count")
      .eq("id", data.id)
      .single();

    let total = d2?.match_count ?? ids.length;

    if (total > ids.length && ids.length < data.limit) {
      const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
      total = await recomputeDemandaMatchesInternal(data.id);
      cacheRows = await readCache();
      ids = ((cacheRows ?? []) as Array<{ anuncio_id: string }>).map((r) => r.anuncio_id);
    }


    if (ids.length === 0) return { items: [], total };

    // Interacciones (favorito/descartado) del usuario dueño de la demanda
    const { data: demOwner } = await sb
      .from("demandas")
      .select("contacto_id")
      .eq("id", data.id)
      .single();
    const interMap = new Map<string, { favorito: boolean; descartado: boolean }>();
    if (demOwner?.contacto_id) {
      const { data: c } = await sb
        .from("contactos")
        .select("user_id")
        .eq("id", demOwner.contacto_id)
        .maybeSingle();
      if (c?.user_id) {
        const inter: unknown[] = [];
        for (let i = 0; i < ids.length; i += 200) {
          const { data: part } = await sb
            .from("anuncio_interacciones")
            .select("anuncio_id, favorito, descartado")
            .eq("user_id", c.user_id)
            .in("anuncio_id", ids.slice(i, i + 200));
          inter.push(...(part ?? []));
        }
        for (const it of inter as Array<{ anuncio_id: string; favorito: boolean | null; descartado: boolean | null }>) {
          interMap.set(it.anuncio_id, {
            favorito: !!it.favorito,
            descartado: !!it.descartado,
          });
        }
      }
    }

    const cacheCreatedById = new Map<string, string>();
    for (const r of (cacheRows ?? []) as Array<{ anuncio_id: string; created_at: string }>) {
      cacheCreatedById.set(r.anuncio_id, r.created_at);
    }
    const seenAtMs = dem?.matches_seen_at ? new Date(dem.matches_seen_at as string).getTime() : 0;

    // Opcionalmente enriquecer con datos del anuncio (usado por el admin).
    const detailMap = new Map<
      string,
      {
        referencia: string | null;
        alias: string | null;
        direccion_completa: string | null;
        precio_alquiler: number | null;
        precio_traspaso: number | null;
        precio_venta: number | null;
        t_superficie: number | null;
        cp: string | null;
        oculto: boolean;
      }
    >();
    if (data.includeDetails) {
      const rows: unknown[] = [];
      for (let i = 0; i < ids.length; i += 200) {
        const { data: part, error: aErr } = await sb
          .from("anuncios")
          .select("id, referencia, alias, direccion_completa, precio_alquiler, precio_traspaso, precio_venta, t_superficie, cp, agencia_id")
          .in("id", ids.slice(i, i + 200));
        if (aErr) throw new Error(aErr.message);
        rows.push(...(part ?? []));
      }
      for (const r of rows as Array<{ id: string } & Record<string, unknown>>) {
        // Anuncios de otras agencias (u off-market ajenos): sin alias ni dirección.
        const oculto = anuncioOcultoPara(viewer, r.agencia_id as string | null);
        detailMap.set(r.id, {
          referencia: (r.referencia as string) ?? null,
          alias: oculto ? null : ((r.alias as string) ?? null),
          direccion_completa: oculto ? null : ((r.direccion_completa as string) ?? null),
          cp: (r.cp as string) ?? null,
          oculto,
          precio_alquiler: (r.precio_alquiler as number) ?? null,
          precio_traspaso: (r.precio_traspaso as number) ?? null,
          precio_venta: (r.precio_venta as number) ?? null,
          t_superficie: (r.t_superficie as number) ?? null,
        });
      }
    }

    const order = new Map(ids.map((id, i) => [id, i]));
    const sorted = ids
      .sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))
      .map((rid) => {
        const st = interMap.get(rid);
        const createdAt = cacheCreatedById.get(rid);
        const isNew = !!createdAt && new Date(createdAt).getTime() > seenAtMs;
        const det = detailMap.get(rid);
        return {
          id: rid,
          favorito: st?.favorito ?? false,
          descartado: st?.descartado ?? false,
          is_new_match: isNew,
          referencia: det?.referencia ?? null,
          alias: det?.alias ?? null,
          direccion_completa: det?.direccion_completa ?? null,
          precio_alquiler: det?.precio_alquiler ?? null,
          precio_traspaso: det?.precio_traspaso ?? null,
          precio_venta: det?.precio_venta ?? null,
          t_superficie: det?.t_superficie ?? null,
          cp: det?.cp ?? null,
          oculto: det?.oculto ?? false,
        };
      });
    return { items: sorted, total };
  });

export const markDemandaMatchesSeen = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data, context }) => {
    const sb = getSupabaseAdmin();
    const { data: c } = await sb
      .from("contactos")
      .select("id")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!c) return { ok: false as const };
    const { error } = await sb
      .from("demandas")
      .update({ matches_seen_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("contacto_id", c.id);
    if (error) throw new Error(error.message);
    // Recorta / cancela el email pendiente si ya no queda nada por notificar.
    try {
      const { pruneDemandaMatchesEmail } = await import("./demanda-email.server");
      await pruneDemandaMatchesEmail(sb, data.id);
    } catch (e) {
      console.error("[markDemandaMatchesSeen] prune email", e);
    }
    return { ok: true as const };
  });




const PreviewInput = z.object({
  operacion_ids: uuidArr,
  tipo_local_ids: uuidArr,
  licencia_ids: uuidArr,
  uso_local_ids: uuidArr,
  provincia_ids: uuidArr,
  ciudad_ids: uuidArr,
  distrito_ids: uuidArr,
  barrio_ids: uuidArr,
  cp: z.string().trim().max(10).nullable().optional(),
  precio_alquiler_min: z.number().nullable().optional(),
  precio_alquiler_max: z.number().nullable().optional(),
  precio_traspaso_min: z.number().nullable().optional(),
  precio_traspaso_max: z.number().nullable().optional(),
  precio_venta_min: z.number().nullable().optional(),
  precio_venta_max: z.number().nullable().optional(),
  rent_bruta_min: z.number().nullable().optional(),
  rent_bruta_max: z.number().nullable().optional(),
  superficie_min: z.number().nullable().optional(),
  superficie_max: z.number().nullable().optional(),
  aseos_min: z.number().int().nullable().optional(),
  parking_plazas_min: z.number().int().nullable().optional(),
  mesas_terraza_min: z.number().int().nullable().optional(),
  mesas_terraza_max: z.number().int().nullable().optional(),
  visibilidades: z.array(z.enum(ANUNCIO_VISIBILIDADES)).default([]),
  origenes: z.array(z.enum(ANUNCIO_ORIGENES)).default([]),
  estado_inmueble: z.array(z.enum(ESTADOS_INMUEBLE)).default([]),
  niveles_transito: z.array(z.enum(NIVELES_TRANSITO)).default([]),
  ...BoolFieldsShape,
  ...RangeFieldsShape,

  limit: z.number().int().min(1).max(500).default(100),
});

export const previewDemandaMatches = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => PreviewInput.parse(d))
  .handler(async ({ data }) => {
    // Solo staff y miembros de agencia; alias y dirección solo de anuncios propios.
    const viewer = await userStaffInfo(await resolveCurrentUserId());
    const sb = getSupabaseAdmin();
    const blockedAgenciaIds = await fetchBlockedAgenciaIds(sb);
    const q = applyDemandaFilters(
      sb,
      data as unknown as DemandaRow,
      "id, referencia, alias, direccion_completa, estado, visibilidad, origen, precio_alquiler, precio_traspaso, precio_venta, t_superficie, cp, created_at, agencia_id",
      { limit: data.limit, blockedAgenciaIds },
    );
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return ((rows ?? []) as unknown as Array<Record<string, unknown>>).map(({ agencia_id, ...r }) =>
      anuncioOcultoPara(viewer, agencia_id as string | null)
        ? { ...r, alias: null, direccion_completa: null, oculto: true }
        : { ...r, oculto: false },
    );
  });


export const recomputeDemandaMatches = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertCanEdit(data.id);
    const { recomputeDemandaMatchesInternal } = await import("@/lib/demandas.server");
    const total = await recomputeDemandaMatchesInternal(data.id);
    return { ok: true, count: total };
  });
